import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Text } from '@/shared/ui';
import { PropertiesService, type PropertyVideoFile } from '@/services/requests/housien-services/PropertiesService';
import { parseVideoLink } from '@/shared/utils/property-videos';
import { SectionCard, EditCardButton, EmptyCardHint } from '../SectionCard';
import type { PropertyDrawerSection } from '../../PropertyDrawer/drawerSections';

interface PropertyVideosSectionProps {
  agencyId?: string;
  propertyId?: string;
  /** The YouTube and Vimeo links saved on the property (`media.videos`). */
  links?: string[];
  /**
   * Changes whenever the property is refetched, so files uploaded or removed
   * in the drawer show up here once it saves.
   */
  refreshKey?: unknown;
  onEditSection?: (section: PropertyDrawerSection) => void;
}

const frameClass = 'w-full aspect-video rounded-(--radius-md) bg-(--surface-tertiary) border-0';

/**
 * Loads the property's uploaded video files; a failed load shows none.
 */
function usePropertyVideoFiles(agencyId: string | undefined, propertyId: string | undefined, refreshKey: unknown) {
  const [files, setFiles] = useState<PropertyVideoFile[]>([]);
  useEffect(() => {
    if (!agencyId || !propertyId) return;
    let alive = true;
    PropertiesService.getPropertyVideos(agencyId, propertyId)
      .then((res) => { if (alive) setFiles(res.videos ?? []); })
      .catch(() => { if (alive) setFiles([]); });
    return () => { alive = false; };
  }, [agencyId, propertyId, refreshKey]);
  return files;
}

/**
 * One saved link: the provider's player, or a plain link when the URL is not
 * a video page the player understands.
 */
const LinkVideo: React.FC<{ link: string; title: string; openLabel: string }> = ({ link, title, openLabel }) => {
  const embed = parseVideoLink(link);
  if (!embed) {
    return (
      <Text variant="bodySm" className="m-0 break-all">
        <Anchor href={link} target="_blank" rel="noopener noreferrer" className="text-[var(--seed-base)]">
          {openLabel}: {link}
        </Anchor>
      </Text>
    );
  }
  return (
    <iframe
      src={embed.embedUrl}
      title={title}
      className={frameClass}
      loading="lazy"
      allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
};

/**
 * The overview card with every video of the property: links play in the
 * provider's player, uploaded files in the browser's own. Hidden when there
 * is nothing to show and no way to add one.
 */
export const PropertyVideosSection: React.FC<PropertyVideosSectionProps> = ({
  agencyId,
  propertyId,
  links = [],
  refreshKey,
  onEditSection,
}) => {
  const { t } = useTranslation('features/properties');
  const files = usePropertyVideoFiles(agencyId, propertyId, refreshKey);
  const empty = links.length === 0 && files.length === 0;
  if (empty && !onEditSection) return null;

  return (
    <SectionCard
      title={t('detailView.videos.title')}
      data-testid="property-videos-card"
      titleAccessory={
        onEditSection ? (
          <EditCardButton onClick={() => onEditSection('videos')} data-testid="property-videos-edit" />
        ) : undefined
      }
    >
      {empty ? (
        <EmptyCardHint />
      ) : (
        <div className="grid gap-(--space-3) sm:grid-cols-2">
          {links.map((link, i) => (
            <LinkVideo
              key={link}
              link={link}
              title={t('detailView.videos.linkTitle', { index: i + 1 })}
              openLabel={t('detailView.videos.openLink')}
            />
          ))}
          {files.map((video, i) => (
            <video
              key={video.id}
              src={video.url}
              controls
              preload="metadata"
              aria-label={t('detailView.videos.fileLabel', { index: i + 1 })}
              className={`${frameClass} object-contain`}
            />
          ))}
        </div>
      )}
    </SectionCard>
  );
};
